/**
 * Theme registry. Maps a theme name (matching the backend allowlist
 * in `app.api.v1.ui_prefs.ALLOWED_THEMES`) to MUI Theme objects for
 * both RTL and LTR directions, plus the sidebar color tokens that
 * theme uses.
 *
 * Adding a new theme:
 *   1. Drop a new file alongside dark.ts / sepia.ts / windowsRetro.ts
 *   2. Register it here in THEME_BY_NAME + THEME_OPTIONS.
 *   3. Add the name to ALLOWED_THEMES on the backend.
 */
import type { Theme } from '@mui/material/styles';

import { rtlTheme as modernRtl, ltrTheme as modernLtr, SIDEBAR_COLORS as MODERN_SIDEBAR } from './index';
import { darkRtlTheme, darkLtrTheme, DARK_SIDEBAR_COLORS } from './dark';
import { sepiaRtlTheme, sepiaLtrTheme, SEPIA_SIDEBAR_COLORS } from './sepia';
import { winRetroRtlTheme, winRetroLtrTheme, WIN_RETRO_SIDEBAR_COLORS } from './windowsRetro';

export type ThemeName = 'modern' | 'dark' | 'sepia' | 'windows-retro';

type SidebarColors = typeof MODERN_SIDEBAR;

interface ThemeEntry {
  rtl: Theme;
  ltr: Theme;
  sidebar: SidebarColors;
}

const THEME_BY_NAME: Record<ThemeName, ThemeEntry> = {
  'modern':         { rtl: modernRtl,       ltr: modernLtr,       sidebar: MODERN_SIDEBAR },
  'dark':           { rtl: darkRtlTheme,    ltr: darkLtrTheme,    sidebar: DARK_SIDEBAR_COLORS as SidebarColors },
  'sepia':          { rtl: sepiaRtlTheme,   ltr: sepiaLtrTheme,   sidebar: SEPIA_SIDEBAR_COLORS as SidebarColors },
  'windows-retro':  { rtl: winRetroRtlTheme, ltr: winRetroLtrTheme, sidebar: WIN_RETRO_SIDEBAR_COLORS as SidebarColors },
};

export function resolveTheme(name: string | null | undefined, isRtl: boolean): { theme: Theme; sidebar: SidebarColors } {
  const key = (name as ThemeName) in THEME_BY_NAME ? (name as ThemeName) : 'modern';
  const entry = THEME_BY_NAME[key];
  return { theme: isRtl ? entry.rtl : entry.ltr, sidebar: entry.sidebar };
}

export const THEME_OPTIONS: Array<{
  name: ThemeName;
  label: string;
  description: string;
  swatch: { bg: string; surface: string; primary: string; text: string };
}> = [
  {
    name: 'modern', label: 'מודרני',
    description: 'ברירת המחדל — בהיר ונקי',
    swatch: { bg: '#F8FAFC', surface: '#FFFFFF', primary: '#2563EB', text: '#0F172A' },
  },
  {
    name: 'dark', label: 'כהה',
    description: 'מצב לילה — נעים לעיניים בעבודות ארוכות',
    swatch: { bg: '#0F172A', surface: '#1E293B', primary: '#60A5FA', text: '#F1F5F9' },
  },
  {
    name: 'sepia', label: 'נייר',
    description: 'גוון חום-קרם בסגנון ספר מודפס, להקלה על העיניים',
    swatch: { bg: '#F5EFE2', surface: '#FBF6E9', primary: '#9A5B2A', text: '#3B2F1E' },
  },
  {
    name: 'windows-retro', label: 'Windows קלאסי',
    description: 'נראה כמו תוכנה של Microsoft Access — מי שמתגעגע',
    swatch: { bg: '#C0C0C0', surface: '#ECECEC', primary: '#000080', text: '#000000' },
  },
];
